/**
 * The Prototype window (UI/UX's, Figma's play mode): the board's screens clicked through at
 * their device's size. The prototype is a document of plain HTML (`prototype/html.ts`) in
 * a frame that may run no script (`sandbox="allow-same-origin"`), played from here
 * (`playPrototype`): its inputs take typing, its checkboxes tick, long screens scroll, clicks lead
 * where the board says, Back goes the way it came. A change on the board shows at once, on the
 * same screen. "Export" saves it as one HTML file that plays by itself.
 *
 * Stable hooks: `#prototype`, `#prototype-frame`, `#prototype-screen`, `#prototype-back`,
 * `#prototype-restart`, `#prototype-from`, `#prototype-export`.
 */
import { For, Show, createEffect, createSignal, on, onCleanup, type JSX } from "solid-js";
import { usePack } from "../../app/context";
import { toast } from "../../shared/toast";
import { playPrototype, type PlayedScreen, type Player } from "./prototype";
import type { UiUx } from "./service";

/** The window. */
export function PrototypeWindow(): JSX.Element {
  const uiux = usePack<UiUx>("uiux");
  const { prototype } = uiux;
  let frame: HTMLIFrameElement | undefined;
  let player: Player | null = null;
  const [shown, setShown] = createSignal<PlayedScreen | null>(null);
  /** The screen to come back to once the document is made again (the board changed). */
  let resume: string | null = null;

  /** Plays the document just loaded. */
  const load = () => {
    player?.stop();
    player = null;
    const doc = frame?.contentDocument;
    if (!doc?.getElementById("device")) return;
    const known = (id: string | null) => (id && prototype.model().screens.some((s) => s.id === id) ? id : null);
    const start = known(resume) ?? prototype.start() ?? prototype.model().start;
    resume = null;
    player = playPrototype(doc, { start, onScreen: setShown });
  };
  // The board changed: the document is made again; stay on the same screen.
  createEffect(on(prototype.html, () => (resume = shown()?.id ?? null), { defer: true }));
  createEffect(
    on(
      prototype.restarts,
      () => {
        const start = prototype.start() ?? prototype.model().start;
        if (start) player?.go(start);
      },
      { defer: true },
    ),
  );
  onCleanup(() => player?.stop());

  const exportSite = () => void prototype.exportSite().then((ok) => toast(ok ? "The prototype was saved as one page that plays by itself." : "No screen yet: draw a frame on the board first."));

  return (
    <div class="prototype-window" id="prototype">
      <Show
        when={uiux.active()}
        fallback={<p class="muted">The prototype is UI/UX's: its frames are screens and its buttons lead from one to another. Work in UI/UX to play it.</p>}
      >
        <Show
          when={prototype.model().screens.length}
          fallback={<p class="muted">No screen yet. On the board, draw a frame (Shift+F; a click makes a phone screen), put buttons on it, then in prototype mode (Shift+E) drag from a button to another screen.</p>}
        >
          <div class="prototype-bar">
            <button type="button" class="btn" id="prototype-back" disabled={!shown()?.back} onClick={() => player?.back()} title="Back the way you came">
              ‹ Back
            </button>
            <span class="prototype-screen" id="prototype-screen">
              {shown()?.name ?? ""}
            </span>
            <select id="prototype-from" aria-label="Start from" value={prototype.start() ?? ""} onChange={(e) => (prototype.setStart(e.currentTarget.value || null), prototype.restart())}>
              <option value="">start screen</option>
              <For each={prototype.model().screens}>{(s) => <option value={s.id}>{s.name}</option>}</For>
            </select>
            <button type="button" class="btn" id="prototype-restart" onClick={() => prototype.restart()} title="From the start again, every field emptied">
              ↺ Restart
            </button>
            <button type="button" class="btn" id="prototype-export" onClick={exportSite} title="One HTML file that plays by itself, to share">
              Export site
            </button>
          </div>
          <iframe ref={frame} id="prototype-frame" class="prototype-frame" title="Prototype" sandbox="allow-same-origin" srcdoc={prototype.html()} onLoad={load} />
        </Show>
      </Show>
    </div>
  );
}
